/* eslint-disable react/prop-types */ // TODO: add props
import { useState, useEffect } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import {
  AnnotatedProgressBar,
  AnnotatedTriStateProgressBar,
} from 'edrolo-style-guide';
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import QSRPT from '../../../propTypes';
import { EXAM_QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';

import style from './StudentResponseBars.scss';

const ProgressContainer = ({ title, children }) => (
  <div className={style.progressContainer}>
    <span className={style.progressBarTitle}>{title}</span>
    {children}
  </div>
);

export const AnsweredProgress = ({ studentCount, responseCount }) => (
  <ProgressContainer title="Answered:">
    <AnnotatedProgressBar
      value={responseCount}
      total={studentCount}
      tooltipText={
        `${responseCount} out of ${studentCount} students have answered ` +
        `this question`
      }
    >
      <span
        className={style.semibold}
      >{`${responseCount}/${studentCount}`}</span>
      <span> students</span>
    </AnnotatedProgressBar>
  </ProgressContainer>
);

export const AnsweredCorrectlyProgress = ({
  responseCount,
  correctResponseCount,
}) => (
  <ProgressContainer title="Answered correctly:">
    <AnnotatedProgressBar
      value={correctResponseCount}
      total={responseCount}
      tooltipText={`${correctResponseCount} of the ${responseCount} responses are correct`}
    >
      <span
        className={style.semibold}
      >{`${correctResponseCount}/${responseCount}`}</span>
      <span> students</span>
    </AnnotatedProgressBar>
  </ProgressContainer>
);

export const SelfReflectionProgress = ({ profileQuestionResponseSummary }) => {
  const responseCount = profileQuestionResponseSummary.filter((row) =>
    row.get('answer')
  ).size;

  const okReflectionCount = profileQuestionResponseSummary.filter(
    (row) => row.get('grokked') === QUESTION_GROKKED.YES
  ).size;
  const needsHelpReflectionCount = profileQuestionResponseSummary.filter(
    (row) => row.get('grokked') === QUESTION_GROKKED.NO
  ).size;
  const missingReflectionCount = profileQuestionResponseSummary.filter(
    (row) => row.get('grokked') === QUESTION_GROKKED.NO_RESPONSE
  ).size;

  return (
    <ProgressContainer title="Self-reflection:">
      <AnnotatedTriStateProgressBar
        negativeCount={needsHelpReflectionCount}
        nullCount={missingReflectionCount}
        positiveCount={okReflectionCount}
        order={['negative', 'positive', 'null']}
        tooltipText={
          `${needsHelpReflectionCount} students need help, ` +
          `${okReflectionCount} students are confident in their understanding, ` +
          `and ${missingReflectionCount} students have not self-reflected`
        }
      >
        <span>
          <span
            className={style.semibold}
          >{`${needsHelpReflectionCount}/${responseCount} `}</span>
          need help
        </span>
      </AnnotatedTriStateProgressBar>
    </ProgressContainer>
  );
};

export const StudentResponseBars = ({
  profileQuestionResponseSummary,
  showCorrectness = false,
  questionType = '',
}) => {
  const [responseCount, setResponseCount] = useState(0);
  const [correctResponseCount, setCorrectResponseCount] = useState(0);

  const studentCount = profileQuestionResponseSummary.size;

  useEffect(() => {
    switch (questionType) {
      case EXAM_QUESTION_TYPES.NUMBER_ON_IMAGE:
      case EXAM_QUESTION_TYPES.FILL_IN_BLANK:
      case EXAM_QUESTION_TYPES.DRAG_AND_DROP:
      case EXAM_QUESTION_TYPES.ILLUSTRATION: {
        const newResponseCount = profileQuestionResponseSummary.filter(
          (row) =>
            row.get('response') &&
            row.getIn(['response', 'field_responses']).size > 0
        ).size;

        setResponseCount(newResponseCount);

        const newCorrectResponseCount = profileQuestionResponseSummary.filter(
          (row) =>
            row.get('response') &&
            row.getIn(['response', 'field_responses']).size > 0 &&
            row.get('correct')
        ).size;

        setCorrectResponseCount(newCorrectResponseCount);

        break;
      }

      default: {
        const newResponseCount = profileQuestionResponseSummary.filter((row) =>
          row.get('answer')
        ).size;
        setResponseCount(newResponseCount);

        const newCorrectResponseCount = profileQuestionResponseSummary.filter(
          (row) => row.get('answer') && row.get('correct')
        ).size;
        setCorrectResponseCount(newCorrectResponseCount);
      }
    }
  }, [questionType, profileQuestionResponseSummary]);

  return (
    <div>
      <AnsweredProgress
        studentCount={studentCount}
        responseCount={responseCount}
      />
      {showCorrectness && (
        <AnsweredCorrectlyProgress
          responseCount={responseCount}
          correctResponseCount={correctResponseCount}
        />
      )}
      <SelfReflectionProgress
        profileQuestionResponseSummary={profileQuestionResponseSummary}
      />
    </div>
  );
};

StudentResponseBars.propTypes = {
  profileQuestionResponseSummary: IPT.listOf(
    QSRPT.ProfileQuestionResponseSummaryRow
  ).isRequired,
  showCorrectness: PT.bool,
};
